import { Component, OnInit, OnDestroy, ViewChild, AfterViewInit, ChangeDetectionStrategy, NgZone } from "@angular/core";
import { DataTablesModule } from "angular-datatables/src/angular-datatables.module";
import { ComponentBase } from "src/app/main/dynamic/component-base";
import { AttributeModel } from "src/app/main/dynamic/model/attribute.model";
import { MaskApplierService } from "ngx-mask";
import { OptionModel } from "src/app/main/dynamic/model/option.model";
import { Subject } from "rxjs";
import { DataTableDirective } from "angular-datatables";

@Component({
  selector: "app-datatable",
  templateUrl: "./datatable.component.html",
  styleUrls: ["./datatable.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatatableComponent extends ComponentBase implements OnInit, OnDestroy, AfterViewInit {
  dtOptions: any = {};

  @ViewChild(DataTableDirective, { static: false })
  dtElement: DataTableDirective;
  dtTrigger: Subject<void> = new Subject<void>();
  private isDestroyed = false;

  items: AttributeModel[] = [];
  itemKey: string;
  itemTitle: string;
  itemsTitles: string[] = [];

  constructor(maskService: MaskApplierService, private ngZone: NgZone) {
    super(maskService);
  }

  ngOnInit(): void {
    const isMobile = window.matchMedia('(max-width: 576px)').matches;

    // Ajusta a quantidade de números na paginação apenas no mobile
    try {
      const anyWin: any = window as any;
      const jQueryRef = (anyWin.$ || anyWin.jQuery);
      if (isMobile && jQueryRef && jQueryRef.fn && jQueryRef.fn.dataTable && jQueryRef.fn.dataTable.ext && jQueryRef.fn.dataTable.ext.pager) {
        jQueryRef.fn.dataTable.ext.pager.numbers_length = 3;
      }
    } catch {}

    this.dtOptions = {
      pagingType: isMobile ? "simple_numbers" : "full_numbers",
      pageLength: 10,
      responsive: true,
      scrollX: true,
      searching: true,
      deferRender: true,
      searchDelay: 300,
      processing: true,
      orderClasses: false,
      lengthMenu: [
        [5, 10, 25, 50, -1],
        ["5", "10", "25", "50", "Todas"],
      ],
      language: {
        url: "https://cdn.datatables.net/plug-ins/1.10.19/i18n/Portuguese-Brasil.json",
        paginate: isMobile ? { first: "<<", previous: "<", next: ">", last: ">>" } : undefined,
      },
      ordering: true,
      autoWidth: false,
      bSort: true,
      dom: "Bfrtip",
      buttons: [
        {
          extend: "excel",
          text: '<i class="fa fa-file-excel-o"></i> Excel',
          className: "btn btn-outline-success mr-1",
          exportOptions: {
            columns: ":visible",
          },
          title: `Export_${this.getCurrentDate()}`,
        },
        {
          extend: "pdf",
          text: '<i class="fa fa-file-pdf-o"></i> PDF',
          className: "btn btn-outline-danger",
          exportOptions: {
            columns: ":visible",
          },
          title: `Export_${this.getCurrentDate()}`,
          orientation: "landscape",
        },
      ],
    } as any;

    let itemsAttr = this.attribute.attributes.find(
      (attr) => attr.type.toLowerCase() == "items"
    );

    if (!itemsAttr) {
      itemsAttr = {
        key: "items",
        type: "items",
      } as any;
      this.attribute.attributes.push(itemsAttr);
    }

    this.itemKey = itemsAttr.key;
    this.items = itemsAttr.attributes || [];

    this.resetCaches();

    this.rebuildHeaders();

  }

  private getCurrentDate(): string {
    const date = new Date();
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}${month}${year}`;
  }

  private getItemTitleRecursive(
    item: AttributeModel,
    titles: string[],
    depth: number = 0
  ) {
    if (item) {
      const canView = !item.options.some(
        (opt) =>
          opt.key.toLowerCase() == "viewmode" &&
          opt.value.toLowerCase() == "hidden"
      );

      if (canView) {
        titles.push(`${this.getItemKey(item)}`);
      }

      if (depth <= 2) {
        depth++;

        item.attributes.forEach((childItem) =>
          this.getItemTitleRecursive(childItem, titles, depth)
        );
      }
    }
  }

  private getItemKey(item: AttributeModel) {
    var titleOpt = item.options.find((opt) => opt.key.toLowerCase() == "title");

    if (titleOpt) {
      return titleOpt.value;
    }

    return item.key;
  }

  private rebuildHeaders(): void {
    this.itemsTitles = [];
    if (this.items && this.items.length > 0) {
      this.getItemTitleRecursive(this.items[0], this.itemsTitles);
      if (this.itemsTitles.length > 0) {
        this.itemsTitles.shift();
      }
    } else {
      const templateAttr = this.attribute?.attributes?.find(
        (attr) => attr.type?.toLowerCase() === "template"
      );
      if (templateAttr) {
        this.getItemTitleRecursive(templateAttr, this.itemsTitles);
        if (this.itemsTitles.length > 0) {
          this.itemsTitles.shift();
        }
      }
    }
  }

  getItemValue(item: AttributeModel): string {
    if (!item || !item.type) {
      return item?.value ?? "";
    }

    const type = item.type.toLowerCase();

    if (type === "collection") {
      return this.getCollectionValue(item);
    } else if (type === "collectionmultiple") {
      return this.getCollectionMultipleValue(item);
    } else if (type === "number") {
      return this.getMaskedNumber(item);
    } else if (type === "processselect") {
      return this.getProcessSelectValue(item);
    } else {
      return this.getMaskedValue(item);
    }
  }

  private getMaskedNumber(item: AttributeModel): string {
    if (this.isNullOrEmpty(item.value)) return "";
    return this.maskService.applyMask(item.value, `separator`);
  }

  private getProcessSelectValue(item: AttributeModel): string {
    const selectedOption = item.attributes.find(
      (attr) => attr.type.toLowerCase() === "processselectedoption"
    );

    if (selectedOption) {
      return selectedOption.value || "";
    }

    return "";
  }

  private getMaskedValue(item: AttributeModel): string {
    const maskOpt = item.options.find((opt) => opt.key.toLowerCase() == "mask");

    if (maskOpt) {
      if (this.isNullOrEmpty(item.value)) return "";
      let maskPattern = "";
      const maskValue = maskOpt.value.toLowerCase();

      if (maskValue === "date" || maskValue === "date br") {
        const date = new Date(item.value);
        if (isNaN(date.getTime())) return "";
        return this.formatDateToBR(date);
      } else if (maskValue === "document") {
        maskPattern = this.getDocumentMask(item);
      } else if (maskValue === "phone") {
        maskPattern = this.getPhoneMask(item);
      } else if (maskValue === "currency") {
        maskPattern = this.getCurrencyMaskText(item);
      } else {
        maskPattern = maskOpt.value;
      }

      const masked = this.maskService.applyMask(item.value, maskPattern);
      return this.applyPrefix(masked, maskOpt);
    }

    return item.value;
  }

  private getCollectionValue(item: AttributeModel): string {
    const foundOption = item.attributes.find((attr) => attr.key == item.value);
    if (foundOption) {
      return foundOption.value;
    } else {
      return item.value;
    }
  }

  private getCollectionMultipleValue(item: AttributeModel): string {
    const selecteds = item.value ? item.value.split(',') : [];

    return selecteds
      .map((key) => {
        const foundItem = item.attributes.find((i) => i.key == key);

        if (foundItem) {
          return foundItem.value;
        } else {
          return key;
        }
      })
      .join(", ");
  }

  private isNullOrEmpty(value: any): boolean {
    return value === null || value === undefined || `${value}`.trim().length === 0;
  }

  private applyPrefix(value: string, maskOpt: OptionModel): string {
    if (maskOpt && maskOpt.value.toLowerCase() == "currency") {
      return `R$ ${value}`;
    }

    return value;
  }

  getItemTitle(item: AttributeModel): string {
    if (item.options) {
      let titleOpt = item.options.find(
        (opt) => opt.key.toLowerCase() == "title"
      );

      if (titleOpt) {
        return titleOpt.value;
      }
    }

    return this.attribute.key;
  }

  getVisibleAttributes(item: AttributeModel): AttributeModel[] {
    return item.attributes.filter(
      (attr) =>
        !attr.options.some(
          (opt) =>
            opt.key.toLowerCase() == "viewmode" &&
            opt.value.toLowerCase() == "hidden"
        )
    );
  }

  private isDate(item: AttributeModel): boolean {
    if (!item || !item.type) return false;
    return item.type.toLowerCase() === "datetime";
  }

  private getDateSortKey(value: string): string {
    // Suporta: dd/MM/yyyy[ HH:mm[:ss]] e yyyy-MM-dd[ HH:mm[:ss]] (ou 'T')
    if (!value) return "";
    const normalized = value.trim();

    // Formato ISO/americano: yyyy-MM-dd[ HH:mm[:ss]] ou com 'T'
    const isoMatch = normalized.match(/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/);
    if (isoMatch) {
      const parts = normalized.split(/[ T]/);
      const datePartIso = parts[0];
      const timePartIso = parts[1] || "";
      const [y, m, d] = datePartIso.split("-");
      const [hh, mm, ss] = timePartIso ? timePartIso.split(":") : [];
      const hours = (hh || "00").padStart(2, "0");
      const minutes = (mm || "00").padStart(2, "0");
      const seconds = (ss || "00").padStart(2, "0");
      return `${y}${m}${d}${hours}${minutes}${seconds}`;
    }

    // Formato brasileiro: dd/MM/yyyy[ HH:mm[:ss]]
    const brMatch = normalized.match(/^\d{2}\/\d{2}\/\d{4}( \d{2}:\d{2}(:\d{2})?)?$/);
    if (brMatch) {
      const [datePartBr, timePartBr] = normalized.split(" ");
      const [day, month, year] = (datePartBr || "").split("/");
      const [hh, mm, ss] = timePartBr ? timePartBr.split(":") : [];
      const hours = (hh || "00").padStart(2, "0");
      const minutes = (mm || "00").padStart(2, "0");
      const seconds = (ss || "00").padStart(2, "0");
      return `${year}${month.padStart(2, "0")}${day.padStart(2, "0")}${hours}${minutes}${seconds}`;
    }

    // Fallback: tentar Date.parse (para outros formatos suportados pelo ambiente)
    const t = Date.parse(normalized);
    if (!isNaN(t)) {
      const dt = new Date(t);
      const y = dt.getFullYear().toString();
      const m = (dt.getMonth() + 1).toString().padStart(2, "0");
      const d = dt.getDate().toString().padStart(2, "0");
      const hh = dt.getHours().toString().padStart(2, "0");
      const mm = dt.getMinutes().toString().padStart(2, "0");
      const ss = dt.getSeconds().toString().padStart(2, "0");
      return `${y}${m}${d}${hh}${mm}${ss}`;
    }

    // Não reconhecido: retorna o próprio valor para ordenação lexicográfica
    return normalized;
  }

  getSortKey(item: AttributeModel): string | number {
    if (!item) return "";
    if (this.isDate(item)) {
      return this.getDateSortKey(item.value);
    }
    return item.value ?? "";
  }

  private initOrRerenderDataTable(): void {
    this.rebuildHeaders();
    if (!this.itemsTitles || this.itemsTitles.length === 0) {
      return; // sem cabeçalho, evita erro do DataTables
    }
    if (this.isDestroyed) return;
    this.resetCaches();
    if (this.dtElement && (this.dtElement as any).dtInstance) {
      (this.dtElement as any).dtInstance.then((dtInstance: any) => {
        this.ngZone.runOutsideAngular(() => {
          dtInstance.destroy();
          if (!this.isDestroyed) {
            this.dtTrigger.next();
          }
        });
      });
    } else {
      this.ngZone.runOutsideAngular(() => this.dtTrigger.next());
    }
  }

  isLink(item: AttributeModel): boolean {
    return item.type.toLowerCase() == "link";
  }

  onClick(item: AttributeModel) {
    let targetOpt = item.options.find(
      (opt) => opt.key.toLowerCase() == "target"
    );

    let targetValue: string;
    let targetType = "_blank";

    const targetTypeOpt = item.options.find(
      (opt) => opt.key.toLowerCase() == "targettype"
    );

    if (targetTypeOpt) {
      if (!targetTypeOpt.value.startsWith("_")) {
        targetType = `_${targetTypeOpt.value}`;
      } else {
        targetType = targetTypeOpt.value;
      }
    }

    if (targetOpt) {
      targetValue = targetOpt.value;
    } else {
      targetValue = item.value;
    }

    if (targetValue.startsWith("http")) {
      window.open(targetValue, targetType);
    } else {
      this.emit({ name: "turn", data: targetValue });
    }
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.resetCaches();
    if (this.dtTrigger) {
      this.dtTrigger.unsubscribe();
    }
    if (this.dtElement && (this.dtElement as any).dtInstance) {
      (this.dtElement as any).dtInstance.then((dtInstance: any) => {
        if (dtInstance && dtInstance.destroy) {
          this.ngZone.runOutsideAngular(() => dtInstance.destroy());
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.itemsTitles && this.itemsTitles.length > 0) {
      this.ngZone.runOutsideAngular(() => this.dtTrigger.next());
    }
  }

  // Caches para reduzir recomputações no template
  private valueCache = new Map<string, string>();
  private sortKeyCache = new Map<string, string | number>();
  private visibleAttrsCache = new Map<AttributeModel, AttributeModel[]>();

  private buildValueCacheKey(item: AttributeModel): string {
    const key = (item && (item as any).key) || "?";
    const type = (item && (item as any).type) || "?";
    const value = item && (item as any).value;
    // incluir tamanho de attributes para invalidar quando muda
    const attrsLen = item && Array.isArray((item as any).attributes) ? (item as any).attributes.length : 0;
    return `${key}|${type}|${value}|${attrsLen}`;
  }

  private resetCaches(): void {
    this.valueCache.clear();
    this.sortKeyCache.clear();
    this.visibleAttrsCache.clear();
  }

  getItemValueCached(item: AttributeModel): string {
    const cacheKey = this.buildValueCacheKey(item);
    if (this.valueCache.has(cacheKey)) {
      return this.valueCache.get(cacheKey);
    }
    const computed = this.getItemValue(item);
    this.valueCache.set(cacheKey, computed);
    return computed;
  }

  getSortKeyCached(item: AttributeModel): string | number {
    const cacheKey = this.buildValueCacheKey(item);
    if (this.sortKeyCache.has(cacheKey)) {
      return this.sortKeyCache.get(cacheKey) as any;
    }
    const computed = this.getSortKey(item);
    this.sortKeyCache.set(cacheKey, computed);
    return computed;
  }

  getVisibleAttributesCached(item: AttributeModel): AttributeModel[] {
    if (this.visibleAttrsCache.has(item)) {
      return this.visibleAttrsCache.get(item) as AttributeModel[];
    }
    const computed = this.getVisibleAttributes(item);
    this.visibleAttrsCache.set(item, computed);
    return computed;
  }

  getStatusClass(value: string): string {
    if (!value) return "";
    switch (value) {
      case "Em aberto":
        return "dt-badge--open";
      case "Liquidado":
        return "dt-badge--paid";
      case "Em atraso":
        return "dt-badge--late";
      case "Em cartório":
        return "dt-badge--court";
      default:
        return "";
    }
  }

  trackByItem(index: number, item: AttributeModel) {
    return (item && (item as any).key) ? (item as any).key : index;
  }

  trackByAttr(index: number, attr: AttributeModel) {
    return (attr && (attr as any).key) ? (attr as any).key : index;
  }
}


